import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { UserContext } from 'context';
import { useQuery } from 'hooks';
import i18n from 'i18n-js';
import { buildUrl, toQueryString } from 'utils';
import Title from 'components/Title';
import useStyles from './List.styled';
import { Table } from 'components/UI';
import Layout from 'components/Layout';
import { useHistory } from 'react-router-dom';
import OrgAbility from 'components/OrgAbility';
import Tooltip from '@material-ui/core/Tooltip';
import RepeatIcon from '@material-ui/icons/Repeat';
import SearchIcon from '@material-ui/icons/Search';
import { truncateKeywordSpaces } from 'utils/utils';
import { SearchField } from 'components/FormFields';
import IconButton from '@material-ui/core/IconButton';
import { vehicleActions } from 'state/modules/vehicles';
import { ChangeVehicleStatusModal } from 'components/Modals';
import InputAdornment from '@material-ui/core/InputAdornment';
import { VehicleGroupsLabel } from 'components/VehiclesGroups';
import { VehiclesGroupsActions } from 'state/modules/vehiclesGroups';
import { Grid, Box, Paper, Button, Container, Typography } from '@material-ui/core';
import { debounce } from 'lodash';

export const FleetsList = () => {
  const classes = useStyles();
  const { orgType } = useContext(UserContext);
  const dispatch = useDispatch();
  const history = useHistory();
  const urlParams = useQuery();
  const isMounted = useRef(false);
  const abortController = useRef(null);

  const { list, pagination, loadingList } = useSelector((state) => state.vehiclesGroups);
  const sharingType = useSelector(
    (state) => state.config?.settings?.core?.partnership?.sharing_type
  );
  const [openChangeVehiclesStatus, setOpenChangeVehiclesStatus] = useState(false);
  const [vehicleGroupId, setVehicleGroupId] = useState(null);
  const [vehicleGroupTZ, setVehicleGroupTZ] = useState(null);
  const [params, setParams] = useState({
    page: 1,
    limit: 10,
    ...urlParams,
  });

  const ActionsMenu = ({ row }) => {
    const handleClick = () => handleModalOpen(row);

    return (
      <Tooltip title={i18n.t('common.change_status')}>
        <IconButton
          variant="contained"
          aria-haspopup="true"
          onClick={handleClick}
          aria-controls="card-menu"
          className={classes.cardHeaderActions}
          data-cy={`vg-change-status-${row?.name?.replace(' ', '-')}`}
        >
          <RepeatIcon />
        </IconButton>
      </Tooltip>
    );
  };

  /**
   * Table columns
   * Includes fleet and service groups
   * Show or hide columns with omit key
   */
  const columns = [
    {
      name: i18n.t('common.name'),
      selector: 'name',
      sortable: true,
    },
    {
      name: i18n.t('common.time_zone'),
      selector: 'time_zone',
      sortable: true,
    },
    {
      name:
        sharingType === 'FFB' ? i18n.t('common.shared_partners') : i18n.t('common.shared_services'),
      selector: 'shared_services',
      cell: (row) => {
        return (
          <Box
            width="100%"
            display="flex"
            flexWrap="wrap"
            className={classes.truncate}
            onClick={() => handleRowClick(row)}
          >
            {row?.shared_services?.length > 0 ? (
              <>
                {row?.shared_services.map((group, index) => {
                  return (
                    <Typography key={index} variant="span">
                      <Box fontSize={13} mr={0.25} display="inline-block">
                        {group}
                        {index + 1 !== row?.shared_services.length ? ', ' : ''}
                      </Box>
                    </Typography>
                  );
                })}
              </>
            ) : (
              '--'
            )}
          </Box>
        );
      },
      sortable: false,
      grow: 3,
    },
    {
      name: i18n.t('common.actions'),
      cell: (row) => <ActionsMenu row={row} />,
      ignoreRowClick: true,
      allowOverflow: true,
      sortable: false,
    },
  ];

  /**
   * Get vehicles grpups list
   * @param {object} params Request params
   */
  const fetchVehiclesGroups = useCallback(
    (params = {}) => {
      const _params = { ...params };

      if (orgType !== 'msOwnerOrg') {
        _params.group_type = 'CUSTOMER';
      }

      if (_params?.keyword) {
        _params.keyword = truncateKeywordSpaces(_params.keyword);
      }

      // Abort previous if any request
      abortController?.current?.abort();
      // Create new abort controller and send signal
      abortController.current = new AbortController();
      dispatch(
        VehiclesGroupsActions.fetchVehicleGroups(_params, undefined, {
          signal: abortController?.current?.signal,
        })
      );
    },
    [dispatch, orgType]
  );

  const fetchVehiclesGroupsDebounced = useCallback(
    debounce((params) => fetchVehiclesGroups(params), 500),
    [fetchVehiclesGroups]
  );

  // On params change once is mounted, debounced
  useEffect(() => {
    if (isMounted.current && orgType) {
      fetchVehiclesGroupsDebounced(params);
    } else {
      isMounted.current = true;
    }
  }, [fetchVehiclesGroupsDebounced, params, orgType]);

  // Run once on mount, no debounce if orgType is available
  useEffect(() => {
    if (orgType) {
      fetchVehiclesGroups(params);
    }
  }, []);

  const handleParams = (params) => {
    const data = { ...params };
    setParams(data);
    const _params = toQueryString(data);
    history.replace(buildUrl(`/fleets${_params}`));
  };

  /**
   * Handle page change on table
   * @param {number} page Number of the page
   */
  const handlePageChange = (page) => {
    const newParams = { page: page, limit: params.limit };
    handleParams({ ...params, ...newParams });
  };

  /**
   * Handle sort change on table
   * @param {string} column Column of the table
   * @param {string} sortDirection Sort direction
   */
  const handleSort = ({ column, sortDirection }) => {
    const newParams = {
      ...params,
      sort_field: column.selector,
      sort_desc: sortDirection === 'desc' ? true : false,
    };
    handleParams(newParams);
  };

  /**
   * Handle per rows (limit) change on table
   * @param {number} limit New limit value
   */
  const handlePerRowsChange = async (limit) => {
    const newParams = { page: 1, limit: limit };
    handleParams({ ...params, ...newParams });
  };

  /**
   * Handle search filter
   * @param {string} name Name of the field to search
   * @param {string} value Value of the input
   */
  const search = (name, value) => {
    const filters = {
      ...params,
      page: 1,
      [name]: value,
    };
    // remove empty fields
    Object.keys(filters).forEach((key) => !filters[key] && delete filters[key]);
    handleParams({ ...filters });
  };

  /**
   * Handle row click
   * @param {object} row Data of the row selected
   */
  const handleRowClick = (row) => {
    history.push(buildUrl(`/fleets/${row.id}`));
  };

  const handleModalOpen = (row) => {
    dispatch(vehicleActions.resetChangeStatus());
    setVehicleGroupId(row?.id);
    setVehicleGroupTZ(row?.time_zone);
    setOpenChangeVehiclesStatus((prevState) => !prevState);
  };

  return (
    <Layout title={VehicleGroupsLabel({ plural: true })}>
      <Container className={classes.container}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid
              container
              spacing={2}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Grid item sm={8}>
                <Box display="flex" alignItems="center">
                  <Title title={VehicleGroupsLabel({ plural: true })} />
                  <Typography variant="span">
                    <Box className={classes.title}>
                      ({i18n.t('vehicle_groups.service_areas_title')})
                    </Box>
                  </Typography>
                </Box>
              </Grid>
              <OrgAbility subject={['msOwnerOrg']}>
                <Grid item>
                  <Button
                    onClick={() => {
                      dispatch(VehiclesGroupsActions.resetOnboarding());
                      history.push(buildUrl('/fleets/create'));
                    }}
                    style={{ textTransform: 'inherit' }}
                    variant="contained"
                    data-cy="create-fleet-btn"
                    color="primary"
                  >
                    {i18n.t('vehicle_groups.create_service_area')}
                  </Button>
                </Grid>
              </OrgAbility>
            </Grid>
            <Grid container>
              <Grid item xs={12} sm={5} md={5}>
                <Typography variant="span">
                  <Box fontSize={14} mt={1}>
                    {sharingType === 'FFB'
                      ? i18n.t('vehicle_groups.fleet_description_partners')
                      : i18n.t('vehicle_groups.fleet_description')}
                  </Box>
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} style={{ paddingBottom: '25px' }}>
            <Grid
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="flex-end"
              spacing={2}
            >
              <Grid item xs={12} sm={4} md={3}>
                <SearchField
                  label={i18n.t('vehicle_groups.search', {
                    type: VehicleGroupsLabel({ plural: true })?.toLowerCase(),
                  })}
                  field={{
                    onChange: search,
                    name: 'keyword',
                  }}
                  inputProps={{
                    startAdornment: (
                      <InputAdornment
                        classes={{
                          root: classes.searchIcon,
                        }}
                        position="start"
                      >
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Paper elevation={2} className={classes.paper}>
              <Table
                data={list}
                columns={columns}
                onSort={handleSort}
                loading={loadingList}
                handleRowClicked={handleRowClick}
                handlePageChange={handlePageChange}
                totalRows={pagination?.totalRows || 0}
                handlePerRowsChange={handlePerRowsChange}
                page={params.page}
                limit={params.limit}
              />
            </Paper>
          </Grid>
        </Grid>
        {openChangeVehiclesStatus && (
          <ChangeVehicleStatusModal
            timezone={vehicleGroupTZ}
            handleClick={handleModalOpen}
            vehicleGroupId={vehicleGroupId}
            title={i18n.t('vehicle.change_vehicles_status')}
            open={vehicleGroupId !== null && openChangeVehiclesStatus}
          />
        )}
      </Container>
    </Layout>
  );
};
